// "Share (QR)" — one dialog for a location, a room or a device, on both shells.
//
// The code is made by the SERVER (core/share/shareAccess) and lives 5 minutes;
// the countdown is that expiry, not a guess. When it runs out the QR is hidden
// and "New code" asks for another — an expired code on screen would only be
// scanned and refused.
//
// The other person scans it from their phone: Me → Add Shared Device.
//
// ⚠️ It says, above the QR, exactly what anyone who scans it will get. A
// location QR is the whole house — every room, every device, the door locks
// too — and the person holding the screen should see that before they turn it
// round.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Typography,
} from '@mui/material';
import QRCode from 'qrcode';
import { useApp } from '../../core/state/AppContext';
import { createShareCode, describeShare } from '../../core/share/shareAccess';
import { copyText } from '../../core/utils/clipboard';
import { FIT_XS } from './fitDialog';

const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const SCOPE_WORD = { location: 'location', room: 'room', device: 'device' };

/**
 * target: { scope:'location', locationId } | { scope:'room', roomId }
 *       | { scope:'device', uniqueNumber, deviceInstalledId? }
 * name:   what is being shared, for the title while the code loads
 * ble:    true for a BLE lamp parent/group (adds the key note)
 */
export default function ShareQrDialog({ open, onClose, target, name, ble = false }) {
  const { realUserId, userId } = useApp();
  // The signed-in person shares — never the user being viewed AS (admin lens).
  const me = realUserId || userId;

  const [state, setState] = useState({ busy: false, share: null, error: null, img: null });
  const [left, setLeft] = useState(0);
  const [copied, setCopied] = useState(false);

  // ⚠️ KEYED ON CONTENT. Callers pass `target` as an inline object, a new one
  // every render; depending on it directly would mint a fresh code per render.
  const targetKey = target ? JSON.stringify(target) : '';
  const load = useCallback(async () => {
    if (!targetKey) return;
    setState({ busy: true, share: null, error: null, img: null });
    setCopied(false);
    const r = await createShareCode(me, JSON.parse(targetKey));
    if (!r.ok) {
      setState({ busy: false, share: null, error: r.message || "Couldn't make a share code.", img: null });
      return;
    }
    // Error-correction M: survives a phone screen's glare and a slightly
    // out-of-focus camera without making the modules too small to read.
    const img = await QRCode.toDataURL(r.code, { errorCorrectionLevel: 'M', margin: 2, width: 280 })
      .catch(() => null);
    setState({ busy: false, share: r, error: null, img });
  }, [me, targetKey]);

  useEffect(() => {
    if (open) load();
    else setState({ busy: false, share: null, error: null, img: null });
  }, [open, load]);

  // The countdown runs off the server's expiry.
  useEffect(() => {
    const exp = state.share?.expiresAt;
    if (!exp) { setLeft(0); return undefined; }
    const tick = () => setLeft(Math.max(0, Math.round((exp - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [state.share]);

  const s = state.share;
  const expired = !!s && left <= 0;
  const scopeWord = SCOPE_WORD[target?.scope] || 'item';

  return (
    <Dialog open={open} onClose={onClose} {...FIT_XS}>
      <DialogTitle sx={{ pb: 1 }}>
        {`Share ${scopeWord}${name ? ` — ${name}` : ''}`}
      </DialogTitle>
      <DialogContent>
        {state.busy && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        )}

        {state.error && <Alert severity="error">{state.error}</Alert>}

        {s && (
          <>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Anyone who scans this in the next 5 minutes gets access to
              {' '}<b>{describeShare(s)}</b>
              {s.scope === 'location' ? ' — every room and every device in it, door locks included.' : '.'}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  bgcolor: '#fff', p: 1, borderRadius: 2, lineHeight: 0,
                  opacity: expired ? 0.12 : 1, transition: 'opacity .3s',
                }}
              >
                {state.img
                  ? <img src={state.img} width={280} height={280} alt="Share QR code" />
                  : <Box sx={{ width: 280, height: 280 }} />}
              </Box>
              <Typography
                variant="h6"
                color={expired ? 'error' : (left <= 60 ? 'warning.main' : 'text.primary')}
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {expired ? 'Expired' : `Valid for ${mmss(left)}`}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              On the other phone: <b>Me → Add Shared Device</b>, then scan this code
              {' '}(or paste it, using Copy code).
            </Typography>
            {ble && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Bluetooth lamps: removing someone later stops the app handing them the lamp
                keys, but a phone that already has them keeps them until the lamps are re-paired.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {s && !expired && (
          <Button
            onClick={async () => setCopied(await copyText(s.code))}
          >
            {copied ? 'Copied' : 'Copy code'}
          </Button>
        )}
        {(expired || state.error) && <Button onClick={load}>New code</Button>}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
